import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ThemeProvider } from '@mui/material/styles';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import SectionAccordion from './SectionAccordion';
import { menuFormTheme, textInputCss } from '../utils/form-styles';
import { BTN_TEXT_COLOR, CURRENCIES, TEXT_SECONDARY_COLOR, THEME_COLOR4, THEME_COLOR5 } from '../../../../../constants';
import type { TMenuItemFormValues } from '../schemas/menu-item-schema';
import type { TPrinter } from '../types/menu-types';

const currencyList = CURRENCIES as Array<{ code: string; symbol: string }>;

const HEX_RE = /^#[0-9A-Fa-f]{0,6}$/;

type TAdvancedSectionProps = {
  accordionRef: React.RefObject<HTMLElement>;
  printerList: TPrinter[];
  currencyCode: string;
  expanded?: boolean;
  iconColor: string;
  headerBackground: string;
  onToggle?: (expanded: boolean) => void;
};

const AdvancedSection = ({ accordionRef, printerList, currencyCode, expanded, onToggle, iconColor, headerBackground }: TAdvancedSectionProps) => {
  const { register, control, watch } = useFormContext<TMenuItemFormValues>();

  const [watchedName, watchedDescription, watchedPriceValue, watchedTextColor, watchedColor] =
    watch(['name', 'description', 'priceValue', 'textColor', 'color']);

  const currencySymbol = currencyList.find((c) => c.code === currencyCode)?.symbol ?? currencyCode;
  const previewName = watchedName || 'Item name';
  const previewPrice = (watchedPriceValue ?? 0).toFixed(2);

  const handleHexChange = (value: string, onChange: (v: string | null) => void) => {
    if (value === '') {
      onChange(null);
    } else if (HEX_RE.test(value)) {
      onChange(value);
    }
  };

  return (
    <SectionAccordion
      accordionRef={accordionRef}
      title="Advanced settings"
      subtitle="Print name, printer, colours"
      icon={<TuneOutlinedIcon fontSize="small" />}
      iconColor={iconColor}
      headerBackground={headerBackground}
      expanded={expanded}
      onToggle={onToggle}
    >
      <Grid container spacing={2.5}>
        {/* Print name */}
        <Grid item xs={12}>
          <ThemeProvider theme={menuFormTheme}>
            <TextField
              focused
              fullWidth
              id="printName"
              label="Print Display Name"
              {...register('printName')}
              variant="standard"
              sx={textInputCss}
              InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
              inputProps={{ autoComplete: 'none', sx: styles.inputText }}
            />
          </ThemeProvider>
          <Typography sx={styles.hint}>
            Name shown on the kitchen printer ticket — leave blank to use the item name.
          </Typography>
        </Grid>

        {/* Printer */}
        <Grid item xs={12}>
          <Typography style={labelStyle}>Kitchen Printer</Typography>
          <FormControl sx={{ width: '100%' }}>
            <ThemeProvider theme={menuFormTheme}>
              <Controller
                control={control}
                name="printerId"
                render={({ field }) => (
                  <Select
                    variant="standard"
                    sx={{ fontSize: 13, fontFamily: 'GilroyBold', ...textInputCss }}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    displayEmpty
                  >
                    <MenuItem value="" sx={styles.menuItem}>
                      <em>Default Printer</em>
                    </MenuItem>
                    {printerList.map((p) => (
                      <MenuItem key={p._id} value={p._id} sx={styles.menuItem}>
                        {p.displayName || p.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </ThemeProvider>
          </FormControl>
          <Typography sx={styles.hint}>Which printer should this item's order go to.</Typography>
        </Grid>

        {/* Colour pickers */}
        <Grid item xs={12}>
          <Typography sx={styles.subSectionLabel}>Menu appearance colours</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Text colour</Typography>
          <Controller
            control={control}
            name="textColor"
            render={({ field }) => (
              <Grid container direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={3}>
                  <input
                    type="color"
                    value={field.value || '#000000'}
                    onChange={(e) => field.onChange(e.target.value)}
                    style={colorInputStyle}
                  />
                </Grid>
                <Grid item xs={9}>
                  <ThemeProvider theme={menuFormTheme}>
                    <TextField
                      focused
                      fullWidth
                      label="Hex Code"
                      value={field.value || ''}
                      variant="standard"
                      sx={textInputCss}
                      InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
                      inputProps={{ autoComplete: 'none', sx: styles.inputText, maxLength: 7 }}
                      onChange={(e) => handleHexChange(e.target.value, field.onChange)}
                    />
                  </ThemeProvider>
                </Grid>
              </Grid>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography style={labelStyle}>Background colour</Typography>
          <Controller
            control={control}
            name="color"
            render={({ field }) => (
              <Grid container direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
                <Grid item xs={3}>
                  <input
                    type="color"
                    value={field.value || '#ffffff'}
                    onChange={(e) => field.onChange(e.target.value)}
                    style={colorInputStyle}
                  />
                </Grid>
                <Grid item xs={9}>
                  <ThemeProvider theme={menuFormTheme}>
                    <TextField
                      focused
                      fullWidth
                      label="Hex Code"
                      value={field.value || ''}
                      variant="standard"
                      sx={textInputCss}
                      InputLabelProps={{ style: { color: THEME_COLOR5 }, sx: styles.inputLabel }}
                      inputProps={{ autoComplete: 'none', sx: styles.inputText, maxLength: 7 }}
                      onChange={(e) => handleHexChange(e.target.value, field.onChange)}
                    />
                  </ThemeProvider>
                </Grid>
              </Grid>
            )}
          />
        </Grid>

        {/* Preview */}
        <Grid item xs={12}>
          <Typography sx={styles.subSectionLabel}>Preview</Typography>
          <Box
            sx={{
              ...s.previewCard,
              backgroundColor: watchedColor || THEME_COLOR4,
              color: watchedTextColor || BTN_TEXT_COLOR,
            }}
          >
            <Typography sx={s.previewLabel}>Preview</Typography>
            <Typography sx={s.previewTitle}>{previewName}</Typography>
            {watchedDescription && (
              <Typography sx={s.previewDescription}>
                {watchedDescription.length > 60
                  ? `${watchedDescription.slice(0, 60)}...`
                  : watchedDescription}
              </Typography>
            )}
            <Typography sx={s.previewPrice}>
              {currencySymbol}
              {previewPrice}
            </Typography>
          </Box>
          <Typography sx={styles.hint}>
            Customise how this item looks on the digital menu.
          </Typography>
        </Grid>
      </Grid>
    </SectionAccordion>
  );
};

export default AdvancedSection;

const labelStyle = { color: THEME_COLOR5, fontFamily: 'GilroyRegular', fontSize: 12 } as React.CSSProperties;

const colorInputStyle: React.CSSProperties = {
  width: '100%',
  height: '56px',
  border: `1px solid ${THEME_COLOR4}`,
  borderRadius: '12px',
  cursor: 'pointer',
  backgroundColor: '#FFF',
};

const s = {
  previewCard: {
    mt: 1,
    borderRadius: '12px',
    minHeight: 140,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center' as const,
    px: 3,
    py: 2.5,
  },
  previewLabel: { fontFamily: 'GilroyRegular', fontSize: 12, opacity: 0.85, mb: 1 },
  previewTitle: { fontFamily: 'GilroyBold', fontSize: 24, lineHeight: 1.1, mb: 0.75 },
  previewDescription: { fontFamily: 'GilroyRegular', fontSize: 13, maxWidth: 420, mb: 1.5 },
  previewPrice: { fontFamily: 'GilroyBold', fontSize: 20 },
};

const styles = {
  inputLabel: { fontFamily: 'GilroyRegular' },
  inputText: { fontSize: 13, fontFamily: 'GilroySemiBold', my: 0.8 },
  menuItem: { fontSize: 13, fontFamily: 'GilroyBold' },
  hint: { mt: 1, fontFamily: 'GilroyRegular', fontSize: 12, color: TEXT_SECONDARY_COLOR },
  subSectionLabel: { fontFamily: 'GilroySemiBold', fontSize: 13, color: TEXT_SECONDARY_COLOR },
};
